/* DREVOS pre-checkout assurance runtime. No analytics or browser storage. */
  (function () {
    if (customElements.get('drevos-checkout-assurance')) return;

    class DrevosCheckoutAssurance extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';
        this.requestId = 0;
        this.postal = this.querySelector('[data-drevos-postal]');
        this.calculateButton = this.querySelector('[data-drevos-calculate]');
        this.status = this.querySelector('[data-drevos-status]');
        this.results = this.querySelector('[data-drevos-results]');
        this.ratesFieldset = this.querySelector('[data-drevos-rates]');
        this.totalNode = this.querySelector('[data-drevos-final-total]');
        this.details = this.querySelector('[data-drevos-details]');
        this.calculateButton.addEventListener('click', () => this.calculate());
        this.postal.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            this.calculate();
          }
        });
        this.postal.addEventListener('input', () => this.invalidate());
        this.installOverlayProtection();
      }

      disconnectedCallback() {
        if (this.requestController) this.requestController.abort();
        if (this.overlayObserver) this.overlayObserver.disconnect();
        if (this.drawerObserver) this.drawerObserver.disconnect();
      }

      invalidate() {
        if (this.requestController) this.requestController.abort();
        this.requestController = null;
        this.requestId += 1;
        this.setLoading(false);
        this.results.hidden = true;
        this.status.hidden = true;
      }

      normalizedPostal() {
        const raw = this.postal.value.trim().replace(/\s+/g, '');
        if (this.dataset.country === 'PL') {
          const digits = raw.replace(/\D/g, '').slice(0, 5);
          return digits.length === 5 ? digits.slice(0, 2) + '-' + digits.slice(2) : raw;
        }
        if (this.dataset.country === 'DE') return raw.replace(/\D/g, '').slice(0, 5);
        if (this.dataset.country === 'AT') return raw.replace(/\D/g, '').slice(0, 4);
        return raw.slice(0, 10);
      }

      validPostal(postal) {
        if (this.dataset.country === 'PL') return /^\d{2}-\d{3}$/.test(postal);
        if (this.dataset.country === 'DE') return /^\d{5}$/.test(postal);
        if (this.dataset.country === 'AT') return /^\d{4}$/.test(postal);
        return postal.length >= 3;
      }

      isGerman() {
        return this.dataset.language === 'de';
      }

      setStatus(message, state) {
        this.status.textContent = message;
        this.status.dataset.state = state || '';
        this.status.hidden = false;
      }

      setLoading(loading) {
        this.calculateButton.disabled = loading;
        this.calculateButton.textContent = loading
          ? (this.isGerman() ? 'Wird berechnet…' : 'Obliczamy…')
          : (this.isGerman() ? 'Berechnen' : 'Sprawdź');
      }

      shippingParams(postal) {
        const params = new URLSearchParams();
        params.set('shipping_address[zip]', postal);
        params.set('shipping_address[country]', this.dataset.country);
        return params;
      }

      async fetchJson(url, options = {}) {
        const response = await fetch(url, options);
        let payload = null;
        try { payload = await response.json(); } catch (error) { payload = null; }
        if (!response.ok) {
          const details = payload && (payload.message || payload.description);
          throw new Error(details || 'shipping_rate_error');
        }
        return payload;
      }

      async loadRates(postal, signal) {
        const params = this.shippingParams(postal);
        const base = this.dataset.cartUrl.replace(/\/$/, '');
        const encoded = params.toString();
        const directUrl = base + '/shipping_rates.json?' + encoded;

        /* The synchronous endpoint returns monetary values in the cart's
           presentment currency. Shopify's prepare/async endpoints can return
           the shop currency instead, so use them only to warm slow carrier
           rates and then request the localized result again. */
        try {
          const direct = await this.fetchJson(directUrl, {
            headers: { 'Accept': 'application/json' },
            signal: signal
          });
          if (direct && Array.isArray(direct.shipping_rates) && direct.shipping_rates.length) return direct.shipping_rates;
        } catch (error) {
          /* Slow carrier-calculated rates may need the async preparation flow. */
        }

        let preparedRates = [];

        try {
          await this.fetchJson(base + '/prepare_shipping_rates.json', {
            method: 'POST',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
            },
            body: encoded,
            signal: signal
          });

          for (let attempt = 0; attempt < 10; attempt += 1) {
            const payload = await this.fetchJson(base + '/async_shipping_rates.json?' + encoded, {
              headers: { 'Accept': 'application/json' },
              signal: signal
            });
            if (payload && Array.isArray(payload.shipping_rates) && payload.shipping_rates.length) {
              preparedRates = payload.shipping_rates;
              break;
            }
            await new Promise((resolve) => window.setTimeout(resolve, 500));
          }
        } catch (error) {
          /* The final localized request below remains the compatibility fallback. */
        }

        try {
          const localized = await this.fetchJson(directUrl, {
            headers: { 'Accept': 'application/json' },
            signal: signal
          });
          if (localized && Array.isArray(localized.shipping_rates) && localized.shipping_rates.length) return localized.shipping_rates;
        } catch (error) {
          /* Never combine a shop-currency rate with a presentment-currency cart. */
        }

        const expectedCurrency = String(this.dataset.currency || '').toUpperCase();
        const preparedCurrencyMatches = preparedRates.every((rate) => {
          return rate.currency && String(rate.currency).toUpperCase() === expectedCurrency;
        });
        if (preparedRates.length && preparedCurrencyMatches) return preparedRates;
        throw new Error('shipping_currency_mismatch');
      }

      money(cents) {
        try {
          return new Intl.NumberFormat(this.dataset.locale, {
            style: 'currency',
            currency: this.dataset.currency
          }).format(cents / 100);
        } catch (error) {
          return (cents / 100).toFixed(2) + ' ' + this.dataset.currency;
        }
      }

      displayCents(cents) {
        const currency = String(this.dataset.currency || '').toUpperCase();
        if (this.dataset.country === 'DE' && currency === 'EUR') {
          return Math.round(cents / 100) * 100;
        }
        return cents;
      }

      displayMoney(cents) {
        const amount = this.money(this.displayCents(cents));
        return this.dataset.country === 'DE' ? 'ca. ' + amount : amount;
      }

      rateCents(rate) {
        const value = typeof rate.price === 'number' ? rate.price : Number.parseFloat(rate.price);
        return Number.isFinite(value) ? Math.round(value * 100) : 0;
      }

      renderRates(rates) {
        this.ratesFieldset.querySelectorAll('.drevos-checkout-assurance__rate').forEach((node) => node.remove());
        const cartTotal = Number.parseInt(this.dataset.cartTotal, 10) || 0;
        let lowestRate = null;

        rates.forEach((rate) => {
          const cents = this.rateCents(rate);
          if (lowestRate === null || cents < lowestRate) lowestRate = cents;
          const row = document.createElement('div');
          row.className = 'drevos-checkout-assurance__rate';
          row.setAttribute('role', 'listitem');

          const main = document.createElement('span');
          main.className = 'drevos-checkout-assurance__rate-main';
          const name = document.createElement('span');
          name.textContent = rate.presentment_name || rate.name || rate.title || (this.isGerman() ? 'Versand' : 'Dostawa');
          main.append(name);

          const price = document.createElement('strong');
          price.className = 'drevos-checkout-assurance__rate-price';
          price.textContent = this.displayMoney(cents);
          row.append(main, price);
          this.ratesFieldset.appendChild(row);
        });

        this.totalNode.textContent = this.displayMoney(cartTotal + (lowestRate || 0));
        this.results.hidden = false;
      }

      async calculate() {
        if (this.calculateButton.disabled) return;
        const requestId = ++this.requestId;
        const postal = this.normalizedPostal();
        this.postal.value = postal;
        this.results.hidden = true;

        if (!this.validPostal(postal)) {
          let invalidPostal = this.isGerman()
            ? 'Bitte geben Sie eine gültige Postleitzahl ein.'
            : 'Wpisz prawidłowy kod pocztowy.';
          if (this.dataset.country === 'PL') invalidPostal = 'Wpisz prawidłowy kod w formacie 00-001.';
          if (this.dataset.country === 'DE') {
            invalidPostal = this.isGerman()
              ? 'Bitte geben Sie eine gültige fünfstellige Postleitzahl ein.'
              : 'Wpisz prawidłowy 5-cyfrowy kod pocztowy.';
          }
          if (this.dataset.country === 'AT') {
            invalidPostal = this.isGerman()
              ? 'Bitte geben Sie eine gültige vierstellige Postleitzahl ein.'
              : 'Wpisz prawidłowy 4-cyfrowy kod pocztowy.';
          }
          this.setStatus(
            invalidPostal,
            'error'
          );
          this.postal.focus();
          return;
        }

        this.setLoading(true);
        if (this.requestController) this.requestController.abort();
        this.requestController = new AbortController();
        const requestController = this.requestController;
        const requestTimeout = window.setTimeout(() => requestController.abort(), 12000);
        this.setStatus(
          this.isGerman() ? 'Aktuelle Versandtarife werden geladen…' : 'Pobieramy aktualne stawki dostawy…',
          ''
        );

        try {
          const rates = await this.loadRates(postal, requestController.signal);
          if (requestId !== this.requestId || this.postal.value !== postal) return;
          if (!rates.length) {
            this.setStatus(
              this.isGerman()
                ? 'Für diese Postleitzahl wurde kein automatischer Tarif gefunden. Kontaktieren Sie uns — wir prüfen die Lieferung manuell.'
                : 'Nie znaleźliśmy automatycznej stawki dla tego kodu. Napisz do nas — sprawdzimy dostawę ręcznie.',
              'error'
            );
            return;
          }
          this.renderRates(rates);
          this.setStatus(
            this.isGerman() ? 'Aktuelle Versandoptionen gefunden.' : 'Znaleźliśmy aktualne opcje dostawy.',
            'success'
          );
        } catch (error) {
          if (requestId !== this.requestId) return;
          this.setStatus(
            this.isGerman()
              ? 'Der Tarif konnte gerade nicht geladen werden. Sie können fortfahren oder uns direkt kontaktieren.'
              : 'Nie udało się teraz pobrać stawki. Możesz kontynuować lub skontaktować się z nami.',
            'error'
          );
        } finally {
          window.clearTimeout(requestTimeout);
          if (this.requestController === requestController) this.requestController = null;
          if (requestId === this.requestId) this.setLoading(false);
        }
      }

      installOverlayProtection() {
        if (window.__drevosCheckoutOverlayProtection) return;
        window.__drevosCheckoutOverlayProtection = true;

        const binotelSelector = 'iframe[src*="binotel.com"], [id*="bingc" i], [class*="bingc" i], [id*="binotel" i], [class*="binotel" i]';
        const uiStateSelector = [
          'cart-drawer',
          '.drevos-hdr-mobile',
          'mobile-menu .drawer',
          '.pswp',
          '.lightbox',
          '.info-modal',
          '.video-modal',
          '.drevos-foodlb',
          '.drevos-vrlb',
          '.drevos-grlb',
          '.reslb',
          '#shopify-pc__banner',
          '#shopify-pc__prefs',
          'dialog',
          'product-modal'
        ].join(',');

        const nodesIn = (root, selector) => {
          const nodes = [];
          if (root instanceof Element && root.matches(selector)) nodes.push(root);
          if (root && typeof root.querySelectorAll === 'function') nodes.push(...root.querySelectorAll(selector));
          return nodes;
        };

        const markBinotelLaunchers = (root = document) => {
          const launchers = nodesIn(root, binotelSelector);
          launchers.forEach((node) => {
            let current = node;
            let fixedRoot = null;
            for (let depth = 0; current && depth < 7; depth += 1, current = current.parentElement) {
              const style = window.getComputedStyle(current);
              if (style.position === 'fixed') fixedRoot = current;
            }
            if (fixedRoot) fixedRoot.classList.add('drevos-checkout-obstruction');
          });
          return launchers.length;
        };

        const isUiVisible = (node) => {
          if (!node || node.hidden || node.getAttribute('aria-hidden') === 'true') return false;
          const style = window.getComputedStyle(node);
          if (style.display === 'none' || style.visibility === 'hidden') return false;
          return node.getClientRects().length > 0;
        };

        const update = () => {
          const drawer = document.querySelector('cart-drawer');
          const cartActive = document.body.classList.contains('template-cart') || Boolean(drawer && drawer.classList.contains('is-open'));
          const menuActive = document.documentElement.classList.contains('drevos-menu-open') || Boolean(document.querySelector(
            '.drevos-hdr-mobile.is-open, mobile-menu .drawer.is-open'
          ));
          const knownOverlayActive = Boolean(document.querySelector(
            '.pswp--open, .lightbox.is-open, .info-modal.is-open, .video-modal.is-open, .drevos-foodlb:not([hidden]), .drevos-vrlb:not([hidden]), .drevos-grlb:not([hidden]), .reslb:not([hidden]), dialog[open], product-modal[open]'
          ));
          const privacyActive = ['#shopify-pc__banner', '#shopify-pc__prefs'].some((selector) => (
            isUiVisible(document.querySelector(selector))
          ));
          const overlayActive = document.body.classList.contains('drevos-modal-open') || menuActive || knownOverlayActive || privacyActive;
          document.documentElement.classList.toggle('drevos-cart-ui-active', cartActive);
          document.documentElement.classList.toggle('drevos-overlay-ui-active', overlayActive);
        };

        let updateFrame = 0;
        const scheduleUpdate = () => {
          if (updateFrame) return;
          updateFrame = window.requestAnimationFrame(() => {
            updateFrame = 0;
            update();
          });
        };

        // Observe attributes only on the actual UI roots, not on every element in body.
        const observedUiNodes = new WeakSet();
        const uiStateObserver = new MutationObserver(scheduleUpdate);
        const observeUiNodes = (root = document) => {
          let registered = 0;
          nodesIn(root, uiStateSelector).forEach((node) => {
            if (observedUiNodes.has(node)) return;
            observedUiNodes.add(node);
            registered += 1;
            uiStateObserver.observe(node, {
              attributes: true,
              attributeFilter: ['class', 'style', 'hidden', 'open', 'aria-hidden']
            });
          });
          return registered;
        };

        uiStateObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
        uiStateObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        observeUiNodes();

        // Child-list changes are used only to register newly inserted launchers/modals.
        const addedNodesObserver = new MutationObserver((mutations) => {
          let needsUpdate = false;
          mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
              if (!(node instanceof Element)) return;
              if (markBinotelLaunchers(node)) needsUpdate = true;
              if (observeUiNodes(node)) needsUpdate = true;
            });
          });
          if (needsUpdate) scheduleUpdate();
        });
        addedNodesObserver.observe(document.body, { childList: true, subtree: true });

        document.addEventListener('click', scheduleUpdate, true);
        document.addEventListener('keydown', scheduleUpdate, true);
        document.addEventListener('close', scheduleUpdate, true);
        document.addEventListener('drevos:external-script-loaded', () => {
          markBinotelLaunchers();
          scheduleUpdate();
        });

        window.__drevosCheckoutUiStateObserver = uiStateObserver;
        window.__drevosCheckoutAddedNodesObserver = addedNodesObserver;
        markBinotelLaunchers();
        update();
      }
    }

    customElements.define('drevos-checkout-assurance', DrevosCheckoutAssurance);
  })();
